import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { FormattedMessage, useIntl } from 'react-intl';
import ReCAPTCHA from 'react-google-recaptcha';

import * as coreAPI from '../coreApi';
import { markNewsletterSubmitted } from './newsletterGate';
import { trackNewsletterSignup, SIGNUP_SOURCE } from './leadAnalytics';

// ============================================================
// LeadPopup
// Path: src/util/leadpopup.js
//
// Lead-generation popup shown on the home page (NewLandingPage)
// and the Blog page. Triggers after 15 seconds OR 50% scroll
// depth, whichever comes first.
//
// Suppression rules (popup is NOT shown if):
//   - localStorage.regenera_newsletter_submitted is set (user
//     already subscribed via this popup OR the NewsletterSignup
//     section on the Partners page — same flag, set by
//     markNewsletterSubmitted()).
//   - localStorage.regenera_popup_dismissed is set (user clicked
//     "Don't show this again" or closed via X).
//
// On submit: uses the same coreAPI.subscribeToNewsLetter +
// invisible reCAPTCHA flow as NewsletterSignup.js, then calls
// markNewsletterSubmitted() so future Knowledge Hub gated
// downloads also bypass the gate.
//
// ⚠ country: collected but NOT sent in this iteration, matching
// NewsletterSignup. Uncomment the line in onCaptchaResolved once
// the backend forwards it to Mailchimp's COUNTRY merge field.
// ============================================================

const POPUP_DISMISSED_KEY = 'regenera_popup_dismissed';
const NEWSLETTER_SUBMITTED_KEY = 'regenera_newsletter_submitted';
const TIME_TRIGGER_MS = 15000;
const SCROLL_TRIGGER_PERCENT = 50;

// ---------- Styled Components ----------

const Backdrop = styled.div`
    position: fixed;
    inset: 0;
    background-color: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(4px);
    z-index: 1000;
    animation: ldp-fade-in 0.3s ease-in-out;

    @keyframes ldp-fade-in {
        from {
            opacity: 0;
        }
        to {
            opacity: 1;
        }
    }
`;

const PopupContainer = styled.div`
    position: fixed;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    z-index: 1001;
    width: 90vw;
    max-width: 1000px;
    animation: ldp-zoom-in 0.3s ease-in-out;

    @keyframes ldp-zoom-in {
        from {
            opacity: 0;
            transform: translate(-50%, -50%) scale(0.95);
        }
        to {
            opacity: 1;
            transform: translate(-50%, -50%) scale(1);
        }
    }
`;

const PopupModal = styled.div`
    position: relative;
    background-color: #ffffff;
    border-radius: 24px;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
    overflow: hidden;
    max-height: 90vh;
    overflow-y: auto;
`;

const CloseButton = styled.button`
    position: absolute;
    right: 20px;
    top: 20px;
    z-index: 10;
    border-radius: 50%;
    padding: 8px;
    background-color: rgba(255, 255, 255, 0.9);
    color: #004d37;
    border: none;
    cursor: pointer;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    transition: all 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
        background-color: #ffffff;
        box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
        transform: scale(1.1);
    }

    svg {
        width: 20px;
        height: 20px;
    }
`;

const GridContainer = styled.div`
    display: grid;

    @media (min-width: 768px) {
        grid-template-columns: 45% 55%;
    }
`;

const LeftPanel = styled.div`
    background: linear-gradient(135deg, #004d37 0%, #005a42 50%, #00654a 100%);
    padding: 40px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    color: white;
    position: relative;
    overflow: hidden;

    @media (max-width: 768px) {
        padding: 32px 24px;
    }
`;

const DecorativeCircle1 = styled.div`
    position: absolute;
    top: 0;
    right: 0;
    width: 288px;
    height: 288px;
    background-color: rgba(117, 172, 56, 0.2);
    border-radius: 50%;
    filter: blur(64px);
    transform: translate(50%, -50%);
    pointer-events: none;
`;

const DecorativeCircle2 = styled.div`
    position: absolute;
    bottom: 0;
    left: 0;
    width: 224px;
    height: 224px;
    background-color: rgba(222, 245, 186, 0.1);
    border-radius: 50%;
    filter: blur(48px);
    transform: translate(-50%, 50%);
    pointer-events: none;
`;

const DecorativeCircle3 = styled.div`
    position: absolute;
    top: 50%;
    left: 50%;
    width: 160px;
    height: 160px;
    background-color: rgba(110, 164, 76, 0.15);
    border-radius: 50%;
    filter: blur(48px);
    pointer-events: none;
`;

const ContentWrapper = styled.div`
    position: relative;
    z-index: 1;
`;

const Badge = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background-color: #75ac38;
    color: white;
    padding: 8px 16px;
    border-radius: 9999px;
    margin-bottom: 24px;
    font-weight: 600;
    font-size: 14px;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);

    svg {
        width: 16px;
        height: 16px;
    }
`;

const Heading = styled.h2`
    font-weight: 700;
    font-size: 32px;
    margin: 0 0 16px;
    line-height: 1.3;
    color: #ffffff;

    @media (min-width: 1024px) {
        font-size: 36px;
    }
`;

const Subtitle = styled.p`
    font-size: 16px;
    margin: 0 0 32px;
    color: rgba(255, 255, 255, 0.9);
    line-height: 1.6;
`;

const BenefitsList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const BenefitItem = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 12px;
    transition: all 0.2s;

    &:hover .icon-wrapper {
        background-color: rgba(117, 172, 56, 0.3);
    }
`;

const IconWrapper = styled.div`
    margin-top: 2px;
    padding: 10px;
    background-color: rgba(117, 172, 56, 0.2);
    border-radius: 12px;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    svg {
        width: 20px;
        height: 20px;
        color: #def5ba;
    }
`;

const BenefitContent = styled.div`
    flex: 1;
`;

const BenefitTitle = styled.h3`
    font-weight: 600;
    font-size: 16px;
    margin: 0 0 4px;
    color: #ffffff;
    line-height: 1.3;
`;
const BenefitDescription = styled.p`
    font-size: 14px;
    color: rgba(255, 255, 255, 0.8);
    margin: 0;
    line-height: 1.4;
`;

const RightPanel = styled.div`
    padding: 32px 40px;
    background-color: #ffffff;
    display: flex;
    flex-direction: column;
    justify-content: center;

    @media (max-width: 768px) {
        padding: 28px 24px;
    }
`;

const FormHeader = styled.div`
    margin-bottom: 24px;
`;

const FormTitle = styled.h3`
    font-weight: 700;
    font-size: 24px;
    color: #004d37;
    margin: 0 0 8px;
`;

const FormSubtitle = styled.p`
    font-size: 14px;
    color: #6d716f;
    margin: 0;
`;

const Form = styled.form`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const FormGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;

    @media (max-width: 480px) {
        grid-template-columns: 1fr;
    }
`;

const FormGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const Label = styled.label`
    color: #004d37;
    font-size: 12px;
    font-weight: 600;
    margin: 0;
`;

const InputWrapper = styled.div`
    position: relative;

    > svg {
        position: absolute;
        left: 12px;
        top: 50%;
        transform: translateY(-50%);
        width: 16px;
        height: 16px;
        color: #75ac38;
        pointer-events: none;
    }
`;

const Input = styled.input`
    font-size: 14px;
    background-color: #f8fbf6;
    border: 1px solid #cecece;
    color: #004d37;
    height: 44px;
    border-radius: 8px;
    padding: 0 12px;
    padding-left: ${props => (props.hasIcon ? '40px' : '12px')};
    width: 100%;
    transition: all 0.2s;
    outline: none;
    font-family: inherit;
    box-sizing: border-box;

    &::placeholder {
        color: #6d716f;
    }

    &:focus {
        border-color: #75ac38;
        box-shadow: 0 0 0 3px rgba(117, 172, 56, 0.2);
    }

    &.error {
        border-color: #d4183d;
    }
`;

const Select = styled.select`
    font-size: 14px;
    background-color: #f8fbf6;
    border: 1px solid #cecece;
    color: #004d37;
    height: 44px;
    border-radius: 8px;
    padding: 0 12px;
    padding-right: 36px;
    width: 100%;
    transition: all 0.2s;
    outline: none;
    cursor: pointer;
    font-family: inherit;
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    background-image: url("data:image/svg+xml;charset=US-ASCII,%3Csvg width='12' height='8' viewBox='0 0 12 8' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%23004d37' stroke-width='1.5' fill='none' fill-rule='evenodd'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 14px center;
    box-sizing: border-box;

    &:focus {
        border-color: #75ac38;
        box-shadow: 0 0 0 3px rgba(117, 172, 56, 0.2);
    }

    &.error {
        border-color: #d4183d;
    }
`;

const ErrorMessage = styled.span`
    font-size: 12px;
    color: #d4183d;
    margin-top: 2px;
`;

const CheckboxWrapper = styled.div`
    padding-top: 4px;
    display: flex;
    align-items: flex-start;
    gap: 8px;
`;

const CheckboxInput = styled.input`
    width: 18px;
    height: 18px;
    border: 1px solid #cecece;
    border-radius: 4px;
    cursor: pointer;
    flex-shrink: 0;
    margin-top: 2px;
    accent-color: #75ac38;
`;

const CheckboxLabel = styled.label`
    font-weight: 400;
    color: #004d37;
    font-size: 12px;
    line-height: 1.5;
    cursor: pointer;
    margin: 0;

    a {
        color: #75ac38;
        text-decoration: underline;
        transition: color 0.2s;

        &:hover {
            color: #004d37;
        }
    }
`;

const ButtonContainer = styled.div`
    padding-top: 8px;
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const SubmitButton = styled.button`
    font-size: 15px;
    font-weight: 700;
    width: 100%;
    background-color: #dd6b17;
    color: #ffffff;
    height: 48px;
    border-radius: 9999px;
    border: none;
    cursor: pointer;
    transition: background-color 0.2s, box-shadow 0.2s, transform 0.05s;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
    font-family: inherit;

    &:hover:not(:disabled) {
        background-color: #c75d10;
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    }

    &:active:not(:disabled) {
        transform: translateY(1px);
    }

    &:disabled {
        background-color: #cccccc;
        cursor: not-allowed;
        box-shadow: none;
    }
`;

const DismissButton = styled.button`
    width: 100%;
    color: #6d716f;
    font-size: 13px;
    font-weight: 600;
    background: none;
    border: none;
    cursor: pointer;
    transition: color 0.2s;
    font-family: inherit;
    padding: 4px 0;

    &:hover {
        color: #004d37;
    }
`;

const SubmitErrorMessage = styled.div`
    margin-top: 4px;
    padding: 10px 14px;
    border-radius: 8px;
    font-size: 13px;
    text-align: center;
    background-color: #fdecef;
    color: #d4183d;
`;

// ---------- Success state ----------

const SuccessWrap = styled.div`
    text-align: center;
    padding: 48px 32px;

    svg.success-check {
        margin: 0 auto 20px;
        display: block;
    }

    h3 {
        font-weight: 700;
        color: #004d37;
        font-size: 28px;
        line-height: 1.2;
        margin: 0 0 12px;
    }

    p {
        color: #004d37;
        font-size: 16px;
        line-height: 1.5;
        margin: 0 0 24px;
        opacity: 0.85;
    }
`;

// ---------- Icons ----------

const XIcon = () => (
    <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
);

const SparklesIcon = () => (
    <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z" />
        <path d="M5 3v4" />
        <path d="M19 17v4" />
        <path d="M3 5h4" />
        <path d="M17 19h4" />
    </svg>
);

const MailIcon = () => (
    <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <rect width="20" height="16" x="2" y="4" rx="2" />
        <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
    </svg>
);

const UsersIcon = () => (
    <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
);

const UserIcon = () => (
    <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
        <circle cx="12" cy="7" r="4" />
    </svg>
);

const SuccessCheck = () => (
    <svg
        className="success-check"
        width="56"
        height="56"
        viewBox="0 0 56 56"
        xmlns="http://www.w3.org/2000/svg"
    >
        <circle cx="28" cy="28" r="26" fill="#6ea44c" />
        <path
            d="M16 28 L24 36 L40 20"
            stroke="#ffffff"
            strokeWidth="3.5"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

// ---------- Option lists (mirrored from NewsletterSignup) ----------

const COUNTRIES = [
    { value: 'Peru', labelId: 'LeadPopup.Country.Peru' },
    { value: 'Mexico', labelId: 'LeadPopup.Country.Mexico' },
    { value: 'Colombia', labelId: 'LeadPopup.Country.Colombia' },
    { value: 'Chile', labelId: 'LeadPopup.Country.Chile' },
    { value: 'Argentina', labelId: 'LeadPopup.Country.Argentina' },
    { value: 'Ecuador', labelId: 'LeadPopup.Country.Ecuador' },
    { value: 'Bolivia', labelId: 'LeadPopup.Country.Bolivia' },
    { value: 'Brazil', labelId: 'LeadPopup.Country.Brazil' },
    { value: 'Spain', labelId: 'LeadPopup.Country.Spain' },
    { value: 'United States', labelId: 'LeadPopup.Country.UnitedStates' },
    { value: 'Canada', labelId: 'LeadPopup.Country.Canada' },
    { value: 'United Kingdom', labelId: 'LeadPopup.Country.UnitedKingdom' },
    { value: 'Germany', labelId: 'LeadPopup.Country.Germany' },
    { value: 'France', labelId: 'LeadPopup.Country.France' },
    { value: 'Netherlands', labelId: 'LeadPopup.Country.Netherlands' },
    { value: 'Other', labelId: 'LeadPopup.Country.Other' },
];

const LANGUAGES = [
    { code: 'en', labelId: 'LeadPopup.Language.English' },
    { code: 'es', labelId: 'LeadPopup.Language.Spanish' },
];

const EMAIL_REGEX = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

const resolveDefaultLanguage = locale => {
    if (!locale) return 'en';
    const short = locale.toLowerCase().split('-')[0];
    return LANGUAGES.some(l => l.code === short) ? short : 'en';
};

// Has this browser already submitted the newsletter OR dismissed the popup?
const shouldSuppressPopup = () => {
    if (typeof window === 'undefined') return true;
    try {
        if (localStorage.getItem(NEWSLETTER_SUBMITTED_KEY)) return true;
        if (localStorage.getItem(POPUP_DISMISSED_KEY)) return true;
    } catch (e) {
        // localStorage may be unavailable (private mode, etc.) — show popup anyway
    }
    return false;
};

// ============================================================

const LeadPopup = ({ source = SIGNUP_SOURCE.HOMEPAGE_POPUP }) => {
    const intl = useIntl();
    const reCaptchaRef = useRef(null);

    const [isVisible, setIsVisible] = useState(false);
    const [hasTriggered, setHasTriggered] = useState(false);

    const [formData, setFormData] = useState({
        firstName: '',
        surname: '',
        email: '',
        country: '',
        language: resolveDefaultLanguage(intl.locale),
        acceptTerms: true, // checked by default per spec
    });

    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);

    // ---- Trigger logic: 15s timer OR 50% scroll, whichever comes first ----
    useEffect(() => {
        if (shouldSuppressPopup()) return;
        if (hasTriggered) return;

        const show = () => {
            if (hasTriggered) return;
            setHasTriggered(true);
            setIsVisible(true);
        };

        const timer = setTimeout(show, TIME_TRIGGER_MS);

        const onScroll = () => {
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            const docHeight = document.documentElement.scrollHeight - window.innerHeight;
            if (docHeight <= 0) return;
            const percent = (scrollTop / docHeight) * 100;
            if (percent >= SCROLL_TRIGGER_PERCENT) {
                show();
            }
        };

        window.addEventListener('scroll', onScroll, { passive: true });

        return () => {
            clearTimeout(timer);
            window.removeEventListener('scroll', onScroll);
        };
    }, [hasTriggered]);

    // Lock body scroll while popup is open
    useEffect(() => {
        if (typeof document === 'undefined') return;
        if (isVisible) {
            const prev = document.body.style.overflow;
            document.body.style.overflow = 'hidden';
            return () => {
                document.body.style.overflow = prev;
            };
        }
    }, [isVisible]);

    // Close on Escape key
    useEffect(() => {
        if (!isVisible) return;
        const onKey = e => {
            if (e.key === 'Escape') handleClose();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isVisible]);

    const handleClose = () => {
        setIsVisible(false);
    };

    const handleDontShowAgain = () => {
        try {
            localStorage.setItem(POPUP_DISMISSED_KEY, 'true');
        } catch (e) {
            // ignore
        }
        setIsVisible(false);
    };

    const handleChange = e => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));

        if (errors[name]) {
            setErrors(prev => {
                const next = { ...prev };
                delete next[name];
                return next;
            });
        }
        if (submitError) setSubmitError(false);
    };

    const validate = () => {
        const next = {};

        if (!formData.firstName.trim()) next.firstName = 'LeadPopup.Error.FirstNameRequired';
        if (!formData.surname.trim()) next.surname = 'LeadPopup.Error.SurnameRequired';

        if (!formData.email.trim()) {
            next.email = 'LeadPopup.Error.EmailRequired';
        } else if (!EMAIL_REGEX.test(formData.email)) {
            next.email = 'LeadPopup.Error.EmailInvalid';
        }

        if (!formData.country) next.country = 'LeadPopup.Error.CountryRequired';
        if (!formData.language) next.language = 'LeadPopup.Error.LanguageRequired';

        setErrors(next);
        return Object.keys(next).length === 0;
    };

    const onCaptchaResolved = async captchaToken => {
        if (!captchaToken) {
            setIsSubmitting(false);
            setSubmitError(true);
            return;
        }

        const submittedEmail = formData.email.trim();

        try {
            await coreAPI.subscribeToNewsLetter(
                {
                    firstname: formData.firstName.trim(),
                    lastname: formData.surname.trim(),
                    email: submittedEmail,
                    language: formData.language,
                    // ⚠ country: not sent in this iteration. Uncomment
                    // once the backend forwards it to Mailchimp's
                    // COUNTRY merge field.
                    // country: formData.country,
                    statusIfNew: 'subscribed',
                    status: 'subscribed',
                },
                captchaToken
            );

            // Mark this browser as having submitted — same flag used by
            // NewsletterSignup, so Knowledge Hub gated downloads also
            // bypass the gate after this.
            markNewsletterSubmitted(submittedEmail);
            trackNewsletterSignup(source);
            setIsSuccess(true);
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('Lead popup signup failed:', err);
            setSubmitError(true);
        } finally {
            setIsSubmitting(false);
            if (reCaptchaRef.current) reCaptchaRef.current.reset();
        }
    };

    const handleSubmit = e => {
        e.preventDefault();
        setSubmitError(false);

        if (!validate()) return;

        setIsSubmitting(true);
        if (reCaptchaRef.current) {
            reCaptchaRef.current.execute();
        } else {
            setIsSubmitting(false);
            setSubmitError(true);
        }
    };

    if (!isVisible) return null;

    const isSubmitDisabled = isSubmitting || !formData.acceptTerms;

    return (
        <>
            <Backdrop onClick={handleClose} />
            <PopupContainer role="dialog" aria-modal="true" aria-labelledby="leadpopup-heading">
                <PopupModal>
                    <CloseButton
                        onClick={handleClose}
                        aria-label={intl.formatMessage({ id: 'LeadPopup.Close' })}
                    >
                        <XIcon />
                    </CloseButton>

                    <ReCAPTCHA
                        ref={reCaptchaRef}
                        size="invisible"
                        sitekey={process.env.REACT_APP_RECAPTCHA_KEY}
                        onChange={onCaptchaResolved}
                    />

                    {isSuccess ? (
                        <SuccessWrap>
                            <SuccessCheck />
                            <h3>
                                <FormattedMessage id="LeadPopup.Success.Title" />
                            </h3>
                            <p>
                                <FormattedMessage id="LeadPopup.Success.Message" />
                            </p>
                            <SubmitButton
                                type="button"
                                onClick={handleClose}
                                style={{ maxWidth: 280, margin: '0 auto' }}
                            >
                                <FormattedMessage id="LeadPopup.Success.Close" />
                            </SubmitButton>
                        </SuccessWrap>
                    ) : (
                        <GridContainer>
                            {/* Left Panel */}
                            <LeftPanel>
                                <DecorativeCircle1 />
                                <DecorativeCircle2 />
                                <DecorativeCircle3 />

                                <ContentWrapper>
                                    <Badge>
                                        <SparklesIcon />
                                        <FormattedMessage id="LeadPopup.Badge" />
                                    </Badge>

                                    <Heading id="leadpopup-heading">
                                        <FormattedMessage id="LeadPopup.Heading" />
                                    </Heading>

                                    <Subtitle>
                                        <FormattedMessage id="LeadPopup.Subtitle" />
                                    </Subtitle>

                                    <BenefitsList>
                                        <BenefitItem>
                                            <IconWrapper className="icon-wrapper">
                                                <MailIcon />
                                            </IconWrapper>
                                            <BenefitContent>
                                                <BenefitTitle>
                                                    <FormattedMessage id="LeadPopup.Benefit1.Title" />
                                                </BenefitTitle>
                                                <BenefitDescription>
                                                    <FormattedMessage id="LeadPopup.Benefit1.Description" />
                                                </BenefitDescription>
                                            </BenefitContent>
                                        </BenefitItem>

                                        <BenefitItem>
                                            <IconWrapper className="icon-wrapper">
                                                <SparklesIcon />
                                            </IconWrapper>
                                            <BenefitContent>
                                                <BenefitTitle>
                                                    <FormattedMessage id="LeadPopup.Benefit2.Title" />
                                                </BenefitTitle>
                                                <BenefitDescription>
                                                    <FormattedMessage id="LeadPopup.Benefit2.Description" />
                                                </BenefitDescription>
                                            </BenefitContent>
                                        </BenefitItem>

                                        <BenefitItem>
                                            <IconWrapper className="icon-wrapper">
                                                <UsersIcon />
                                            </IconWrapper>
                                            <BenefitContent>
                                                <BenefitTitle>
                                                    <FormattedMessage id="LeadPopup.Benefit3.Title" />
                                                </BenefitTitle>
                                                <BenefitDescription>
                                                    <FormattedMessage id="LeadPopup.Benefit3.Description" />
                                                </BenefitDescription>
                                            </BenefitContent>
                                        </BenefitItem>
                                    </BenefitsList>
                                </ContentWrapper>
                            </LeftPanel>

                            {/* Right Panel */}
                            <RightPanel>
                                <FormHeader>
                                    <FormTitle>
                                        <FormattedMessage id="LeadPopup.Form.Title" />
                                    </FormTitle>
                                    <FormSubtitle>
                                        <FormattedMessage id="LeadPopup.Form.Subtitle" />
                                    </FormSubtitle>
                                </FormHeader>

                                <Form onSubmit={handleSubmit} noValidate>
                                    <FormGrid>
                                        <FormGroup>
                                            <Label htmlFor="leadpopup-firstName">
                                                <FormattedMessage id="LeadPopup.FirstName.Label" />{' '}
                                                *
                                            </Label>
                                            <InputWrapper>
                                                <UserIcon />
                                                <Input
                                                    id="leadpopup-firstName"
                                                    name="firstName"
                                                    type="text"
                                                    placeholder={intl.formatMessage({
                                                        id: 'LeadPopup.FirstName.Placeholder',
                                                    })}
                                                    value={formData.firstName}
                                                    onChange={handleChange}
                                                    className={errors.firstName ? 'error' : ''}
                                                    hasIcon
                                                    autoComplete="given-name"
                                                />
                                            </InputWrapper>
                                            {errors.firstName && (
                                                <ErrorMessage>
                                                    <FormattedMessage id={errors.firstName} />
                                                </ErrorMessage>
                                            )}
                                        </FormGroup>

                                        <FormGroup>
                                            <Label htmlFor="leadpopup-surname">
                                                <FormattedMessage id="LeadPopup.Surname.Label" /> *
                                            </Label>
                                            <Input
                                                id="leadpopup-surname"
                                                name="surname"
                                                type="text"
                                                placeholder={intl.formatMessage({
                                                    id: 'LeadPopup.Surname.Placeholder',
                                                })}
                                                value={formData.surname}
                                                onChange={handleChange}
                                                className={errors.surname ? 'error' : ''}
                                                autoComplete="family-name"
                                            />
                                            {errors.surname && (
                                                <ErrorMessage>
                                                    <FormattedMessage id={errors.surname} />
                                                </ErrorMessage>
                                            )}
                                        </FormGroup>
                                    </FormGrid>

                                    <FormGroup>
                                        <Label htmlFor="leadpopup-email">
                                            <FormattedMessage id="LeadPopup.Email.Label" /> *
                                        </Label>
                                        <InputWrapper>
                                            <MailIcon />
                                            <Input
                                                id="leadpopup-email"
                                                name="email"
                                                type="email"
                                                placeholder={intl.formatMessage({
                                                    id: 'LeadPopup.Email.Placeholder',
                                                })}
                                                value={formData.email}
                                                onChange={handleChange}
                                                className={errors.email ? 'error' : ''}
                                                hasIcon
                                                autoComplete="email"
                                            />
                                        </InputWrapper>
                                        {errors.email && (
                                            <ErrorMessage>
                                                <FormattedMessage id={errors.email} />
                                            </ErrorMessage>
                                        )}
                                    </FormGroup>

                                    <FormGrid>
                                        <FormGroup>
                                            <Label htmlFor="leadpopup-country">
                                                <FormattedMessage id="LeadPopup.Country.Label" /> *
                                            </Label>
                                            <Select
                                                id="leadpopup-country"
                                                name="country"
                                                value={formData.country}
                                                onChange={handleChange}
                                                className={errors.country ? 'error' : ''}
                                            >
                                                <option value="">
                                                    {intl.formatMessage({
                                                        id: 'LeadPopup.Country.Placeholder',
                                                    })}
                                                </option>
                                                {COUNTRIES.map(({ value, labelId }) => (
                                                    <option key={value} value={value}>
                                                        {intl.formatMessage({ id: labelId })}
                                                    </option>
                                                ))}
                                            </Select>
                                            {errors.country && (
                                                <ErrorMessage>
                                                    <FormattedMessage id={errors.country} />
                                                </ErrorMessage>
                                            )}
                                        </FormGroup>

                                        <FormGroup>
                                            <Label htmlFor="leadpopup-language">
                                                <FormattedMessage id="LeadPopup.Language.Label" /> *
                                            </Label>
                                            <Select
                                                id="leadpopup-language"
                                                name="language"
                                                value={formData.language}
                                                onChange={handleChange}
                                                className={errors.language ? 'error' : ''}
                                            >
                                                {LANGUAGES.map(({ code, labelId }) => (
                                                    <option key={code} value={code}>
                                                        {intl.formatMessage({ id: labelId })}
                                                    </option>
                                                ))}
                                            </Select>
                                            {errors.language && (
                                                <ErrorMessage>
                                                    <FormattedMessage id={errors.language} />
                                                </ErrorMessage>
                                            )}
                                        </FormGroup>
                                    </FormGrid>

                                    <CheckboxWrapper>
                                        <CheckboxInput
                                            id="leadpopup-acceptTerms"
                                            name="acceptTerms"
                                            type="checkbox"
                                            checked={formData.acceptTerms}
                                            onChange={handleChange}
                                        />
                                        <CheckboxLabel htmlFor="leadpopup-acceptTerms">
                                            <FormattedMessage
                                                id="LeadPopup.Terms"
                                                values={{
                                                    link: chunks => (
                                                        <a
                                                            href="/privacy-policy"
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                        >
                                                            {chunks}
                                                        </a>
                                                    ),
                                                }}
                                            />
                                        </CheckboxLabel>
                                    </CheckboxWrapper>

                                    {submitError && (
                                        <SubmitErrorMessage>
                                            <FormattedMessage id="LeadPopup.Error.Submission" />
                                        </SubmitErrorMessage>
                                    )}

                                    <ButtonContainer>
                                        <SubmitButton type="submit" disabled={isSubmitDisabled}>
                                            <FormattedMessage
                                                id={
                                                    isSubmitting
                                                        ? 'LeadPopup.Submitting'
                                                        : 'LeadPopup.Submit'
                                                }
                                            />
                                        </SubmitButton>

                                        <DismissButton type="button" onClick={handleDontShowAgain}>
                                            <FormattedMessage id="LeadPopup.DontShowAgain" />
                                        </DismissButton>
                                    </ButtonContainer>
                                </Form>
                            </RightPanel>
                        </GridContainer>
                    )}
                </PopupModal>
            </PopupContainer>
        </>
    );
};

export default LeadPopup;
